export const apiEndpointsData = {
  categories: [
    {
      id: 'authentication',
      name: 'Authentication',
      icon: 'KeyIcon',
      description: 'User authentication and session management',
      endpoints: [
        {
          id: 'post-login',
          method: 'POST',
          path: '/api/auth/login',
          summary: 'User login',
          description: 'Authenticate user with username and password. Returns a JWT token valid for 24 hours.',
          authentication: false,
          requestBody: {
            contentType: 'application/json',
            example: {
              username: 'admin',
              password: 'password123'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              token: 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...',
              user: {
                id: 1,
                username: 'admin',
                full_name: 'Administrator',
                email: 'admin@company.com',
                role: 'admin',
                is_active: 1
              }
            }
          },
          responseError: {
            statusCode: 401,
            example: {
              error: 'Invalid credentials'
            }
          }
        },
        {
          id: 'get-me',
          method: 'GET',
          path: '/api/auth/me',
          summary: 'Get current user',
          description: 'Retrieve information about the currently authenticated user including their permissions.',
          authentication: true,
          responseSuccess: {
            statusCode: 200,
            example: {
              id: 1,
              username: 'admin',
              full_name: 'Administrator',
              email: 'admin@company.com',
              role: 'admin',
              is_active: 1,
              permissions: [
                'vendors_view',
                'vendors_edit',
                'invoices_view',
                'invoices_edit',
                'invoices_approve',
                'users_manage'
              ]
            }
          },
          responseError: {
            statusCode: 401,
            example: {
              error: 'Unauthorized'
            }
          }
        },
        {
          id: 'put-password',
          method: 'PUT',
          path: '/api/auth/password',
          summary: 'Change password',
          description: 'Change the password for the currently authenticated user.',
          authentication: true,
          requestBody: {
            contentType: 'application/json',
            example: {
              currentPassword: 'oldPassword123',
              newPassword: 'newSecurePassword456'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Password updated successfully'
            }
          },
          responseError: {
            statusCode: 400,
            example: {
              error: 'Current password is incorrect'
            }
          }
        },
        {
          id: 'put-profile',
          method: 'PUT',
          path: '/api/auth/profile',
          summary: 'Update profile',
          description: 'Update profile information for the currently authenticated user.',
          authentication: true,
          requestBody: {
            contentType: 'application/json',
            example: {
              full_name: 'John Administrator',
              email: 'john.admin@company.com'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Profile updated successfully',
              user: {
                id: 1,
                username: 'admin',
                full_name: 'John Administrator',
                email: 'john.admin@company.com'
              }
            }
          },
          responseError: {
            statusCode: 400,
            example: {
              error: 'Email already in use'
            }
          }
        }
      ]
    },
    {
      id: 'vendors',
      name: 'Vendors',
      icon: 'BuildingOfficeIcon',
      description: 'Vendor master data management',
      endpoints: [
        {
          id: 'get-vendors',
          method: 'GET',
          path: '/api/vendors',
          summary: 'List vendors',
          description: 'Retrieve a paginated list of vendors with optional search filtering.',
          authentication: true,
          queryParams: [
            { name: 'page', type: 'number', description: 'Page number (default: 1)' },
            { name: 'limit', type: 'number', description: 'Items per page (default: 50)' },
            { name: 'search', type: 'string', description: 'Search by name or vendor code' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              vendors: [
                {
                  id: 1,
                  vendor_code: 'VEND001',
                  name: 'ABC Supplies Co.',
                  contact_person: 'John Smith',
                  email: 'john@abcsupplies.com',
                  phone: '+1-555-0123',
                  address: '123 Business St',
                  city: 'New York',
                  country: 'USA',
                  tax_id: 'TAX123456',
                  payment_terms: 'Net 30',
                  is_active: 1
                }
              ],
              totalPages: 5,
              currentPage: 1,
              totalCount: 45
            }
          }
        },
        {
          id: 'get-vendor',
          method: 'GET',
          path: '/api/vendors/:id',
          summary: 'Get vendor',
          description: 'Retrieve detailed information about a specific vendor.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Vendor ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              id: 1,
              vendor_code: 'VEND001',
              name: 'ABC Supplies Co.',
              contact_person: 'John Smith',
              email: 'john@abcsupplies.com',
              phone: '+1-555-0123',
              address: '123 Business St',
              city: 'New York',
              postal_code: '10001',
              country: 'USA',
              tax_id: 'TAX123456',
              payment_terms: 'Net 30',
              bank_name: 'First Bank',
              bank_account: '****1234',
              notes: 'Preferred supplier',
              is_active: 1
            }
          },
          responseError: {
            statusCode: 404,
            example: {
              error: 'Vendor not found'
            }
          }
        },
        {
          id: 'post-vendor',
          method: 'POST',
          path: '/api/vendors',
          summary: 'Create vendor',
          description: 'Create a new vendor record.',
          authentication: true,
          requestBody: {
            contentType: 'application/json',
            example: {
              vendor_code: 'VEND002',
              name: 'XYZ Corporation',
              contact_person: 'Jane Doe',
              email: 'jane@xyzcorp.com',
              phone: '+1-555-0456',
              address: '456 Commerce Ave',
              city: 'Boston',
              country: 'USA',
              payment_terms: 'Net 60'
            }
          },
          responseSuccess: {
            statusCode: 201,
            example: {
              message: 'Vendor created successfully',
              vendor: {
                id: 2,
                vendor_code: 'VEND002',
                name: 'XYZ Corporation'
              }
            }
          }
        },
        {
          id: 'put-vendor',
          method: 'PUT',
          path: '/api/vendors/:id',
          summary: 'Update vendor',
          description: 'Update an existing vendor record.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Vendor ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              name: 'XYZ Corporation Inc.',
              phone: '+1-555-9999',
              payment_terms: 'Net 45'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Vendor updated successfully'
            }
          }
        },
        {
          id: 'delete-vendor',
          method: 'DELETE',
          path: '/api/vendors/:id',
          summary: 'Delete vendor',
          description: 'Delete a vendor. Returns error if vendor has associated invoices.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Vendor ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Vendor deleted successfully'
            }
          },
          responseError: {
            statusCode: 400,
            example: {
              error: 'Cannot delete vendor with existing invoices'
            }
          }
        },
        {
          id: 'get-vendors-dropdown',
          method: 'GET',
          path: '/api/vendors/list/dropdown',
          summary: 'Vendor dropdown',
          description: 'Get simplified list of active vendors for dropdown selections.',
          authentication: true,
          responseSuccess: {
            statusCode: 200,
            example: [
              { id: 1, name: 'ABC Supplies Co.', vendor_code: 'VEND001' },
              { id: 2, name: 'XYZ Corporation', vendor_code: 'VEND002' }
            ]
          }
        }
      ]
    },
    {
      id: 'customers',
      name: 'Customers',
      icon: 'UserGroupIcon',
      description: 'Customer master data management',
      endpoints: [
        {
          id: 'get-customers',
          method: 'GET',
          path: '/api/customers',
          summary: 'List customers',
          description: 'Retrieve a paginated list of customers with optional search filtering.',
          authentication: true,
          queryParams: [
            { name: 'page', type: 'number', description: 'Page number (default: 1)' },
            { name: 'limit', type: 'number', description: 'Items per page (default: 50)' },
            { name: 'search', type: 'string', description: 'Search by name or customer code' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              customers: [
                {
                  id: 1,
                  customer_code: 'CUST001',
                  name: 'Acme Industries',
                  contact_person: 'Sarah Johnson',
                  email: 'sarah@acme.com',
                  phone: '+1-555-7890',
                  city: 'Chicago',
                  country: 'USA',
                  is_active: 1
                }
              ],
              totalPages: 3,
              currentPage: 1
            }
          }
        },
        {
          id: 'get-customer',
          method: 'GET',
          path: '/api/customers/:id',
          summary: 'Get customer',
          description: 'Retrieve detailed information about a specific customer.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Customer ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              id: 1,
              customer_code: 'CUST001',
              name: 'Acme Industries',
              contact_person: 'Sarah Johnson',
              email: 'sarah@acme.com',
              phone: '+1-555-7890',
              address: '789 Industrial Blvd',
              city: 'Chicago',
              country: 'USA',
              tax_id: 'TAX789012',
              payment_terms: 'Net 30',
              is_active: 1
            }
          }
        },
        {
          id: 'post-customer',
          method: 'POST',
          path: '/api/customers',
          summary: 'Create customer',
          description: 'Create a new customer record.',
          authentication: true,
          requestBody: {
            contentType: 'application/json',
            example: {
              customer_code: 'CUST002',
              name: 'Tech Solutions Ltd',
              contact_person: 'Mike Brown',
              email: 'mike@techsolutions.com',
              phone: '+1-555-3456'
            }
          },
          responseSuccess: {
            statusCode: 201,
            example: {
              message: 'Customer created successfully',
              customer: {
                id: 2,
                customer_code: 'CUST002'
              }
            }
          }
        },
        {
          id: 'put-customer',
          method: 'PUT',
          path: '/api/customers/:id',
          summary: 'Update customer',
          description: 'Update an existing customer record.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Customer ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              email: 'newemail@techsolutions.com',
              payment_terms: 'Net 45'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Customer updated successfully'
            }
          }
        },
        {
          id: 'delete-customer',
          method: 'DELETE',
          path: '/api/customers/:id',
          summary: 'Delete customer',
          description: 'Delete a customer. Returns error if customer has associated invoices.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Customer ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Customer deleted successfully'
            }
          }
        },
        {
          id: 'get-customers-dropdown',
          method: 'GET',
          path: '/api/customers/list/dropdown',
          summary: 'Customer dropdown',
          description: 'Get simplified list of active customers for dropdown selections.',
          authentication: true,
          responseSuccess: {
            statusCode: 200,
            example: [
              { id: 1, name: 'Acme Industries', customer_code: 'CUST001' }
            ]
          }
        }
      ]
    },
    {
      id: 'ap-invoices',
      name: 'AP Invoices',
      icon: 'DocumentTextIcon',
      description: 'Accounts Payable invoice management with approval workflow',
      endpoints: [
        {
          id: 'get-ap-invoices',
          method: 'GET',
          path: '/api/invoices',
          summary: 'List AP invoices',
          description: 'Retrieve a paginated list of AP invoices with advanced filtering options.',
          authentication: true,
          queryParams: [
            { name: 'page', type: 'number', description: 'Page number' },
            { name: 'limit', type: 'number', description: 'Items per page' },
            { name: 'vendor_id', type: 'number', description: 'Filter by vendor' },
            { name: 'status', type: 'string', description: 'Filter by status (draft, pending_approval, approved, sent_to_accounting, paid)' },
            { name: 'date_from', type: 'date', description: 'Filter by invoice date (from)' },
            { name: 'date_to', type: 'date', description: 'Filter by invoice date (to)' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              invoices: [
                {
                  id: 1,
                  invoice_number: 'INV-2024-001',
                  vendor_name: 'ABC Supplies Co.',
                  invoice_date: '2024-01-15',
                  due_date: '2024-02-14',
                  total_amount: 5000.00,
                  currency: 'USD',
                  status: 'approved',
                  created_by: 'john.doe'
                }
              ],
              totalPages: 10,
              currentPage: 1,
              totalCount: 95
            }
          }
        },
        {
          id: 'get-ap-invoice',
          method: 'GET',
          path: '/api/invoices/:id',
          summary: 'Get AP invoice',
          description: 'Retrieve detailed information about an AP invoice including line items and history.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              id: 1,
              invoice_number: 'INV-2024-001',
              vendor_id: 1,
              vendor_name: 'ABC Supplies Co.',
              company_id: 1,
              invoice_date: '2024-01-15',
              due_date: '2024-02-14',
              total_amount: 5000.00,
              currency: 'USD',
              status: 'approved',
              pdf_url: 'https://drive.google.com/file/d/...',
              notes: 'Office supplies order',
              line_items: [
                {
                  id: 1,
                  description: 'Office supplies',
                  quantity: 100,
                  unit_price: 50.00,
                  total_amount: 5000.00,
                  cost_center_id: 1,
                  account_id: 1
                }
              ],
              history: [
                {
                  action: 'created',
                  performed_by: 'john.doe',
                  timestamp: '2024-01-15T10:00:00Z'
                },
                {
                  action: 'approved',
                  performed_by: 'manager',
                  timestamp: '2024-01-16T14:30:00Z'
                }
              ]
            }
          }
        },
        {
          id: 'post-ap-invoice',
          method: 'POST',
          path: '/api/invoices',
          summary: 'Create AP invoice',
          description: 'Create a new AP invoice with PDF attachment. Requires multipart/form-data for file upload.',
          authentication: true,
          requestBody: {
            contentType: 'multipart/form-data',
            example: {
              invoice_number: 'INV-2024-002',
              vendor_id: 1,
              company_id: 1,
              invoice_date: '2024-01-20',
              due_date: '2024-02-20',
              total_amount: 3500.00,
              currency: 'USD',
              pdf: '<file>',
              notes: 'Monthly service fee'
            }
          },
          responseSuccess: {
            statusCode: 201,
            example: {
              message: 'Invoice created successfully',
              invoice: {
                id: 2,
                invoice_number: 'INV-2024-002'
              }
            }
          }
        },
        {
          id: 'put-ap-invoice',
          method: 'PUT',
          path: '/api/invoices/:id',
          summary: 'Update AP invoice',
          description: 'Update an existing AP invoice. Only draft invoices can be edited.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              total_amount: 3750.00,
              notes: 'Updated monthly service fee'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice updated successfully'
            }
          },
          responseError: {
            statusCode: 400,
            example: {
              error: 'Cannot edit invoice in current status'
            }
          }
        },
        {
          id: 'post-ap-invoice-submit',
          method: 'POST',
          path: '/api/invoices/:id/submit',
          summary: 'Submit for approval',
          description: 'Submit an AP invoice for approval. Changes status from draft to pending_approval.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice submitted for approval'
            }
          }
        },
        {
          id: 'post-ap-invoice-approve',
          method: 'POST',
          path: '/api/invoices/:id/approve',
          summary: 'Approve invoice',
          description: 'Approve an AP invoice. Requires invoices_approve permission.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice approved successfully'
            }
          }
        },
        {
          id: 'post-ap-invoice-rework',
          method: 'POST',
          path: '/api/invoices/:id/rework',
          summary: 'Send for rework',
          description: 'Send invoice back to draft for corrections. Requires invoices_approve permission.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              reason: 'Please update cost center allocation'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice sent back for rework'
            }
          }
        },
        {
          id: 'post-ap-invoice-send-to-accounting',
          method: 'POST',
          path: '/api/invoices/:id/send-to-accounting',
          summary: 'Send to accounting',
          description: 'Mark invoice as sent to accounting system. Requires invoices_approve permission.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice sent to accounting'
            }
          }
        },
        {
          id: 'post-ap-invoice-pay',
          method: 'POST',
          path: '/api/invoices/:id/pay',
          summary: 'Mark as paid',
          description: 'Mark invoice as paid. Requires invoices_approve permission.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              payment_date: '2024-02-10',
              payment_reference: 'PAY-2024-001'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice marked as paid'
            }
          }
        },
        {
          id: 'delete-ap-invoice',
          method: 'DELETE',
          path: '/api/invoices/:id',
          summary: 'Delete invoice',
          description: 'Delete an AP invoice. Requires invoices_delete permission.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Invoice deleted successfully'
            }
          }
        },
        {
          id: 'post-ap-line-item',
          method: 'POST',
          path: '/api/invoices/:id/line-items',
          summary: 'Add line item',
          description: 'Add a line item to an AP invoice.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              description: 'Consulting services',
              quantity: 10,
              unit_price: 150.00,
              total_amount: 1500.00,
              cost_center_id: 2,
              account_id: 5
            }
          },
          responseSuccess: {
            statusCode: 201,
            example: {
              message: 'Line item added successfully'
            }
          }
        },
        {
          id: 'put-ap-line-item',
          method: 'PUT',
          path: '/api/invoices/:id/line-items/:lineId',
          summary: 'Update line item',
          description: 'Update an existing line item.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' },
            { name: 'lineId', type: 'number', description: 'Line item ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              quantity: 12,
              total_amount: 1800.00
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Line item updated successfully'
            }
          }
        },
        {
          id: 'delete-ap-line-item',
          method: 'DELETE',
          path: '/api/invoices/:id/line-items/:lineId',
          summary: 'Delete line item',
          description: 'Delete a line item from an invoice.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'Invoice ID' },
            { name: 'lineId', type: 'number', description: 'Line item ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Line item deleted successfully'
            }
          }
        }
      ]
    },
    {
      id: 'ar-invoices',
      name: 'AR Invoices',
      icon: 'BanknotesIcon',
      description: 'Accounts Receivable invoice management',
      endpoints: [
        {
          id: 'get-ar-invoices',
          method: 'GET',
          path: '/api/ar-invoices',
          summary: 'List AR invoices',
          description: 'Retrieve a paginated list of AR invoices with filtering options.',
          authentication: true,
          queryParams: [
            { name: 'page', type: 'number', description: 'Page number' },
            { name: 'limit', type: 'number', description: 'Items per page' },
            { name: 'customer_id', type: 'number', description: 'Filter by customer' },
            { name: 'status', type: 'string', description: 'Filter by status' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              invoices: [
                {
                  id: 1,
                  invoice_number: 'AR-2024-001',
                  customer_name: 'Acme Industries',
                  invoice_date: '2024-01-15',
                  due_date: '2024-02-14',
                  total_amount: 10000.00,
                  currency: 'USD',
                  status: 'sent_to_customer'
                }
              ],
              totalPages: 5,
              currentPage: 1
            }
          }
        },
        {
          id: 'get-ar-invoice',
          method: 'GET',
          path: '/api/ar-invoices/:id',
          summary: 'Get AR invoice',
          description: 'Retrieve detailed information about an AR invoice.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              id: 1,
              invoice_number: 'AR-2024-001',
              customer_id: 1,
              customer_name: 'Acme Industries',
              invoice_date: '2024-01-15',
              due_date: '2024-02-14',
              total_amount: 10000.00,
              currency: 'USD',
              status: 'sent_to_customer',
              line_items: [
                {
                  description: 'Software License',
                  quantity: 5,
                  unit_price: 2000.00,
                  total_amount: 10000.00
                }
              ]
            }
          }
        },
        {
          id: 'post-ar-invoice',
          method: 'POST',
          path: '/api/ar-invoices',
          summary: 'Create AR invoice',
          description: 'Create a new AR invoice.',
          authentication: true,
          requestBody: {
            contentType: 'multipart/form-data',
            example: {
              invoice_number: 'AR-2024-002',
              customer_id: 1,
              invoice_date: '2024-01-20',
              due_date: '2024-02-20',
              total_amount: 8000.00,
              currency: 'USD',
              pdf: '<file>'
            }
          },
          responseSuccess: {
            statusCode: 201,
            example: {
              message: 'AR invoice created successfully'
            }
          }
        },
        {
          id: 'put-ar-invoice',
          method: 'PUT',
          path: '/api/ar-invoices/:id',
          summary: 'Update AR invoice',
          description: 'Update an existing AR invoice.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              total_amount: 8500.00
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR invoice updated successfully'
            }
          }
        },
        {
          id: 'post-ar-invoice-submit',
          method: 'POST',
          path: '/api/ar-invoices/:id/submit',
          summary: 'Submit AR invoice',
          description: 'Submit AR invoice for review.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR invoice submitted'
            }
          }
        },
        {
          id: 'post-ar-invoice-approve',
          method: 'POST',
          path: '/api/ar-invoices/:id/approve',
          summary: 'Approve AR invoice',
          description: 'Approve AR invoice for sending to customer.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR invoice approved'
            }
          }
        },
        {
          id: 'post-ar-invoice-send',
          method: 'POST',
          path: '/api/ar-invoices/:id/send-to-customer',
          summary: 'Send to customer',
          description: 'Mark AR invoice as sent to customer.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR invoice marked as sent to customer'
            }
          }
        },
        {
          id: 'post-ar-invoice-pay',
          method: 'POST',
          path: '/api/ar-invoices/:id/receive-payment',
          summary: 'Receive payment',
          description: 'Mark AR invoice as paid.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              payment_date: '2024-02-10',
              payment_reference: 'PAY-CUST-001'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Payment received'
            }
          }
        },
        {
          id: 'delete-ar-invoice',
          method: 'DELETE',
          path: '/api/ar-invoices/:id',
          summary: 'Delete AR invoice',
          description: 'Delete an AR invoice.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR invoice deleted successfully'
            }
          }
        },
        {
          id: 'post-ar-line-item',
          method: 'POST',
          path: '/api/ar-invoices/:id/line-items',
          summary: 'Add AR line item',
          description: 'Add a line item to an AR invoice.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              description: 'Consulting hours',
              quantity: 20,
              unit_price: 200.00,
              total_amount: 4000.00
            }
          },
          responseSuccess: {
            statusCode: 201,
            example: {
              message: 'AR line item added'
            }
          }
        },
        {
          id: 'put-ar-line-item',
          method: 'PUT',
          path: '/api/ar-invoices/:id/line-items/:lineId',
          summary: 'Update AR line item',
          description: 'Update an AR invoice line item.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' },
            { name: 'lineId', type: 'number', description: 'Line item ID' }
          ],
          requestBody: {
            contentType: 'application/json',
            example: {
              quantity: 25,
              total_amount: 5000.00
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR line item updated'
            }
          }
        },
        {
          id: 'delete-ar-line-item',
          method: 'DELETE',
          path: '/api/ar-invoices/:id/line-items/:lineId',
          summary: 'Delete AR line item',
          description: 'Delete an AR invoice line item.',
          authentication: true,
          pathParams: [
            { name: 'id', type: 'number', description: 'AR Invoice ID' },
            { name: 'lineId', type: 'number', description: 'Line item ID' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'AR line item deleted'
            }
          }
        },
        {
          id: 'get-ar-export',
          method: 'GET',
          path: '/api/ar-invoices/export/excel',
          summary: 'Export to Excel',
          description: 'Export AR invoices to Excel file.',
          authentication: true,
          queryParams: [
            { name: 'status', type: 'string', description: 'Filter by status' },
            { name: 'date_from', type: 'date', description: 'Filter from date' },
            { name: 'date_to', type: 'date', description: 'Filter to date' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Returns Excel file download'
            }
          }
        }
      ]
    },
    {
      id: 'settings',
      name: 'Settings',
      icon: 'Cog6ToothIcon',
      description: 'System settings management',
      endpoints: [
        {
          id: 'get-settings',
          method: 'GET',
          path: '/api/settings',
          summary: 'Get all settings',
          description: 'Retrieve all system settings. Admin only.',
          authentication: true,
          responseSuccess: {
            statusCode: 200,
            example: {
              google_drive_ap_folder_id: {
                value: '1a2b3c4d5e6f7g8h9i0j',
                updated_at: '2024-01-15T10:00:00Z'
              },
              google_drive_ar_folder_id: {
                value: '9i8h7g6f5e4d3c2b1a0j',
                updated_at: '2024-01-15T10:00:00Z'
              },
              google_service_account_email: {
                value: 'service@project.iam.gserviceaccount.com',
                updated_at: '2024-01-15T10:00:00Z'
              }
            }
          },
          responseError: {
            statusCode: 403,
            example: {
              error: 'Admin access required'
            }
          }
        },
        {
          id: 'get-setting',
          method: 'GET',
          path: '/api/settings/:key',
          summary: 'Get setting by key',
          description: 'Retrieve a specific system setting. Admin only.',
          authentication: true,
          pathParams: [
            { name: 'key', type: 'string', description: 'Setting key name' }
          ],
          responseSuccess: {
            statusCode: 200,
            example: {
              setting_key: 'google_drive_ap_folder_id',
              setting_value: '1a2b3c4d5e6f7g8h9i0j',
              description: 'Google Drive folder ID for AP invoices',
              updated_by: 1,
              updated_at: '2024-01-15T10:00:00Z'
            }
          }
        },
        {
          id: 'post-setting',
          method: 'POST',
          path: '/api/settings',
          summary: 'Create/update setting',
          description: 'Create or update a system setting. Admin only.',
          authentication: true,
          requestBody: {
            contentType: 'application/json',
            example: {
              setting_key: 'google_drive_ap_folder_id',
              setting_value: '1a2b3c4d5e6f7g8h9i0j',
              description: 'Google Drive folder ID for AP invoices'
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Setting saved successfully'
            }
          }
        },
        {
          id: 'post-settings-batch',
          method: 'POST',
          path: '/api/settings/batch',
          summary: 'Batch update settings',
          description: 'Update multiple settings at once. Admin only.',
          authentication: true,
          requestBody: {
            contentType: 'application/json',
            example: {
              settings: [
                {
                  setting_key: 'google_service_account_email',
                  setting_value: 'service@project.iam.gserviceaccount.com',
                  description: 'Google service account email'
                },
                {
                  setting_key: 'google_private_key',
                  setting_value: '-----BEGIN PRIVATE KEY-----\n...',
                  description: 'Google service account private key'
                }
              ]
            }
          },
          responseSuccess: {
            statusCode: 200,
            example: {
              message: 'Settings updated successfully'
            }
          }
        }
      ]
    }
  ]
};
